/**
 * lib/permissions.ts
 * Role metadata and per-role navigation access. 'admin' (and the
 * legacy 'operator') have full access including user management.
 */
import type { UserRole } from '@/types';

export const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'System Admin',
  operator: 'System Admin',
  manager: 'Manager',
  bookkeeper: 'Bookkeeper',
  sales: 'Sales',
  viewer: 'Viewer',
};

export const ROLE_DESCRIPTION: Record<UserRole, string> = {
  admin: 'Full access, including managing users.',
  operator: 'Full access, including managing users.',
  manager: 'Full access except user management.',
  bookkeeper: 'Clients, invoices, payments, quotes, credit notes, expenses, compliance & documents.',
  sales: 'Clients, quotes, leads and communications.',
  viewer: 'Read-only dashboard.',
};

/** Roles an admin can assign when creating a user. */
export const ASSIGNABLE_ROLES: UserRole[] = ['manager', 'bookkeeper', 'sales', 'viewer', 'admin'];

export function isAdmin(role: UserRole | null | undefined): boolean {
  return role === 'admin' || role === 'operator';
}

/** Nav keys each non-admin role may open. Admin/operator get everything. */
const ACCESS: Record<string, Set<string>> = {
  manager: new Set([
    'dashboard', 'comms', 'clients', 'invoices', 'payments', 'quotes',
    'recurring', 'credit', 'statements', 'letters', 'compliance', 'leads', 'expenses', 'settings',
  ]),
  bookkeeper: new Set([
    'dashboard', 'clients', 'invoices', 'payments', 'quotes', 'recurring',
    'credit', 'statements', 'expenses', 'compliance', 'letters',
  ]),
  sales: new Set(['dashboard', 'clients', 'quotes', 'leads', 'comms']),
  viewer: new Set(['dashboard']),
};

export function canAccess(role: UserRole | null | undefined, navKey: string): boolean {
  if (isAdmin(role)) return true;
  if (!role) return false;
  return ACCESS[role]?.has(navKey) ?? false;
}
